<template>
    <div class="enquiry-form">
        <AuthFormInModal
            :is-open="!!enquiryType && !hideForm"
            :modal-label="formTitle || 'Enquiry form'"
            :registration-disabled="fetching"
            :registration-errors="errors"
            @close="closeEnquiryForm"
            @register="submit($event)"
            @login="submit(() => {})"
        >
            <template #registration-title>
                {{ formTitle }}
            </template>

            <template #registration-submit>
                {{ formSubmitLabel }}
            </template>
        </AuthFormInModal>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'

import { useEnquiryFormStore } from '@/stores/enquiryForm'
import { useEnquiryFormTranslations } from './useEnquiryFormTranslations'

import { ROUTE_PAGE } from '@/router'
import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import { getCommissionJunctionInput } from '@/helpers/commission-junction'
import { getUri } from '@/helpers/getCurrentUri'
import {
    createResponseAndPath,
    useLastResponseErrorAtPath,
} from '@/helpers/errors'
import { useHandleEnquiryResponse } from '@/composables/enquiry/useHandleEnquiryResponse'
import { useRegionPlugin } from '@/plugins/region'

import { useIsAdmin } from '@/components/app/isAdmin'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useAuth } from '@/plugins/auth.ts'
import { useCreateEnquiryNewCustomerMutation } from '@/components/enquiry/api/mutations/createEnquiryNewCustomer.generated.ts'
import { useCreateEnquiryExistingCustomerMutation } from '@/components/enquiry/api/mutations/createEnquiryExistingCustomer.generated.ts'

import AuthFormInModal from '../user/auth/AuthFormInModal.vue'

const enquiryFormStore = useEnquiryFormStore()
const { closeEnquiryForm } = enquiryFormStore
const { enquiryType, enquiryImage, isFetching } = storeToRefs(enquiryFormStore)

const translations = useEnquiryFormTranslations()

const formTitle = computed(
    () => enquiryType.value && translations[enquiryType.value]?.title()
)

const formSubmitLabel = computed(
    () => enquiryType.value && translations[enquiryType.value]?.submit()
)

const commissionJunction = getCommissionJunctionInput()

const uri = getUri()
const currency = useCurrencyInput()
const { regionCode } = useRegionPlugin()
const { isLogged } = useAuth()

const newCustomerResponse = useCreateEnquiryNewCustomerMutation()
const { executeMutation: newCustomerMutation, fetching: newCustomerFetching } =
    newCustomerResponse

const existingCustomerResponse = useCreateEnquiryExistingCustomerMutation()
const {
    executeMutation: existingCustomerMutation,
    fetching: existingCustomerFetching,
} = existingCustomerResponse

const fetching = computed(
    () => newCustomerFetching.value || existingCustomerFetching.value
)

watch(fetching, (n) => (isFetching.value = n))

const errors = useLastResponseErrorAtPath([
    createResponseAndPath(
        existingCustomerResponse,
        'createEnquiryExistingCustomer'
    ),
    createResponseAndPath(newCustomerResponse, 'createEnquiryNewCustomer'),
])

const { handleEnquiryResponse } = useHandleEnquiryResponse()

const enquiryInput = computed(() => {
    if (!enquiryType.value) {
        return null
    }

    return {
        baseInput: {
            uri,
            currency: currency.value,
            region: regionCode.value,
        },
        image: enquiryImage.value,
        uri,
        type: enquiryType.value,
        commissionJunction,
    }
})

const hideForm = ref(false)

watch(
    enquiryType,
    (type) => {
        const input = enquiryInput.value

        if (!input) {
            return
        }

        if (!type) {
            return
        }

        if (!isLogged.value) {
            return
        }

        hideForm.value = true

        existingCustomerMutation({ input }).then(({ data, error }) => {
            if (error) {
                hideForm.value = false
                // Error state should be displayed at this point
                return
            }

            if (data && data.createEnquiryExistingCustomer) {
                handleEnquiryResponse({
                    data: {
                        customer: undefined,
                    },
                    error,
                })

                closeEnquiryForm()
                hideForm.value = false
            }
        })
    },
    { flush: 'post' }
)

const { customerRegistrationInput, googleSignInToken, facebookSignInToken } =
    storeToRefs(useCustomerRegistrationInputStore())

const isAdmin = useIsAdmin()

const doSubmit = () => {
    const input = enquiryInput.value

    if (!input) {
        return
    }

    if (isLogged.value) {
        return existingCustomerMutation({ input }).then(({ data, error }) => {
            if (error) {
                return undefined
            }

            return data?.createEnquiryExistingCustomer
        })
    }

    const customer = customerRegistrationInput.value

    if (!customer) {
        return
    }

    return newCustomerMutation({
        input,
        customer,
        isConsultant: isAdmin.value,
    }).then(({ data, error }) => {
        if (error) {
            googleSignInToken.value = null
            facebookSignInToken.value = null

            return undefined
        }

        return data?.createEnquiryNewCustomer
    })
}

const route = useRoute()

const getTypeformRedirect = () => {
    if (enquiryType.value !== 'ENGAGEMENT_RING') {
        return undefined
    }

    if (route.name !== ROUTE_PAGE) {
        return undefined
    }

    const slug = route.params?.['slug']

    if (slug !== '' && slug?.[0] !== 'custom-engagement-rings') {
        return undefined
    }

    switch (regionCode.value) {
        case 'UK':
            return 'https://taylorandhart.com/type-form/view/dHNtS0jG'
        case 'US':
            return 'https://taylorandhart.com/us/type-form/view/R1ZlBnod'
        case 'INT':
            return 'https://taylorandhart.com/int/type-form/view/WuvS2Sd5'
        default:
            return undefined
    }
}

const submit = (successCallback: () => void) =>
    doSubmit()?.then((data) => {
        if (!data) {
            // Error state should be displayed at this point
            return
        }

        const redirect = getTypeformRedirect()

        handleEnquiryResponse({
            data:
                data.__typename === 'EnquiryResponseExistingCustomer'
                    ? undefined
                    : data,
            error: undefined,
            redirect,
        })

        if (
            data.__typename === 'EnquiryResponseNewCustomer' &&
            data.customer.login
        ) {
            successCallback()
        }

        closeEnquiryForm()
    })
</script>
